import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FiX, FiUpload, FiTrash2, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import api from '../utils/api';

const ISSUE_TYPES = [
  { value: '', label: 'Select issue type...' },
  { value: 'Bug', label: 'Bug' },
  { value: 'DataIssue', label: 'Data Issue' },
  { value: 'FeatureBroken', label: 'Feature / Broken' },
  { value: 'FeatureRequest', label: 'Feature Request' },
  { value: 'UiUx', label: 'UI / UX' },
  { value: 'WorkflowQuestion', label: 'Workflow Question' },
  { value: 'TrainingQuestion', label: 'Training Question' },
  { value: 'Billing', label: 'Billing' },
  { value: 'LoginAccess', label: 'Login / Access' },
  { value: 'Integration', label: 'Integration' },
  { value: 'Performance', label: 'Performance' },
  { value: 'Faxing', label: 'Faxing' },
  { value: 'FormsDocuments', label: 'Forms / Documents' },
  { value: 'Messaging', label: 'Messaging' },
  { value: 'Scheduling', label: 'Scheduling' },
  { value: 'Reporting', label: 'Reporting' },
  { value: 'Other', label: 'Other / Request' },
];

const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];

const PHI_STATUS_OPTIONS = [
  {
    value: 'NoPhiExpected',
    label: 'No, this does not include client-identifying or health information.',
  },
  {
    value: 'MayContainPhi',
    label: 'It might include client-identifying or health information.',
  },
  {
    value: 'ContainsPhi',
    label: 'Yes, this includes client-identifying or health information.',
  },
];

const MAX_FILES = 2;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

const initialForm = {
  type: '',
  severity: 'Medium',
  title: '',
  description: '',
  phiStatus: '',
};

export default function ReportIssueModal({
  onClose,
  prefillDescription = '',
  ticketReferenceId = null,
}) {
  const isEditMode = Boolean(ticketReferenceId);

  const [form, setForm] = useState({
    ...initialForm,
    description: isEditMode ? '' : prefillDescription,
  });
  const [files, setFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [loadingTicket, setLoadingTicket] = useState(isEditMode);
  const [loadError, setLoadError] = useState('');
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!ticketReferenceId) return undefined;

    let cancelled = false;
    (async () => {
      setLoadingTicket(true);
      setLoadError('');
      try {
        const t = await api.fetchTicketById(ticketReferenceId);
        if (cancelled) return;
        setForm({
          type: t.type || '',
          severity: t.severity || 'Medium',
          title: t.title || '',
          description: t.description || '',
          phiStatus: t.phiStatus || '',
        });
        setExistingAttachments(Array.isArray(t.attachments) ? t.attachments : []);
      } catch (err) {
        console.error('Load ticket error:', err);
        if (!cancelled) {
          setLoadError(err.message || 'Could not load this ticket. Please try again.');
        }
      } finally {
        if (!cancelled) setLoadingTicket(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ticketReferenceId]);

  const handleField = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setSubmitError('');
  };

  const handleSeverity = (severity) => {
    setForm((prev) => ({ ...prev, severity }));
  };

  const attachmentSlotsUsed = existingAttachments.length + files.length;

  const validateFile = useCallback(
    (file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        return 'Only PNG and JPG files are allowed.';
      }
      if (file.size > MAX_FILE_SIZE) {
        return `${file.name} exceeds the 5 MB limit.`;
      }
      if (attachmentSlotsUsed >= MAX_FILES) {
        return `Maximum ${MAX_FILES} screenshots allowed.`;
      }
      return null;
    },
    [attachmentSlotsUsed]
  );

  const handleFileSelect = (e) => {
    setFileError('');
    const selected = Array.from(e.target.files || []);
    for (const file of selected) {
      const err = validateFile(file);
      if (err) {
        setFileError(err);
        e.target.value = '';
        return;
      }
    }
    const remaining = MAX_FILES - attachmentSlotsUsed;
    const toAdd = selected.slice(0, remaining);
    setFiles((prev) => [
      ...prev,
      ...toAdd.map((f) => ({ file: f, preview: URL.createObjectURL(f) })),
    ]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].preview);
      next.splice(index, 1);
      return next;
    });
    setFileError('');
  };

  const canSubmit =
    form.type &&
    form.severity &&
    form.title.trim() &&
    form.description.trim() &&
    form.phiStatus &&
    !submitting &&
    (!isEditMode || (!loadingTicket && !loadError));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      let targetReferenceId = ticketReferenceId;

      if (isEditMode) {
        await api.updateTicket(ticketReferenceId, {
          type: form.type,
          severity: form.severity,
          title: form.title.trim(),
          description: form.description.trim(),
          phiStatus: form.phiStatus,
        });
      } else {
        const ticket = await api.createTicket({
          type: form.type,
          severity: form.severity,
          title: form.title.trim(),
          description: form.description.trim(),
          phiStatus: form.phiStatus,
        });
        targetReferenceId = ticket.referenceId;
      }

      if (files.length > 0 && targetReferenceId) {
        for (const { file } of files) {
          await api.uploadAttachment(targetReferenceId, file);
        }
      }

      setSuccess(true);
      setTimeout(() => onClose(), 3000);
    } catch (err) {
      console.error('Ticket submission error:', err);
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  if (success) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-xl max-w-md w-full p-8 text-center shadow-xl">
          <FiCheckCircle className="mx-auto h-12 w-12 text-[#4f7a64] mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {isEditMode ? 'Changes Saved' : 'Report Sent'}
          </h2>
          <p className="text-sm text-gray-600">
            {isEditMode
              ? 'Your ticket has been updated.'
              : 'Thanks! Your report has been sent to the Sena team.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditMode ? 'Edit Issue' : 'Report an Issue'}
          </h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {loadingTicket && (
          <div className="px-6 py-16 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4f7a64]" />
          </div>
        )}

        {loadError && !loadingTicket && (
          <div className="px-6 py-8 space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <FiAlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {loadError}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-[#4f7a64] hover:bg-[#3d6350] rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        {!loadingTicket && !loadError && (
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* PHI status */}
          <fieldset>
            <legend className="block mb-2 text-sm font-medium text-gray-700">
              Does this ticket include client-identifying or health information?
              <span className="text-red-500">*</span>
            </legend>
            <div className="space-y-2">
              {PHI_STATUS_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className="flex items-start gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="radio"
                    name="phiStatus"
                    value={option.value}
                    checked={form.phiStatus === option.value}
                    onChange={handleField}
                    className="mt-1"
                    required
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500 leading-relaxed">
              Please avoid including client names, dates of birth, medical details, screenshots with
              client information, or documents unless they are necessary for support.
            </p>
          </fieldset>

          {form.phiStatus && (
          <>
          {/* Issue Type */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Issue Type<span className="text-red-500">*</span>
            </label>
            <select
              name="type"
              value={form.type}
              onChange={handleField}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50"
            >
              {ISSUE_TYPES.map((t) => (
                <option key={t.value} value={t.value} disabled={!t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Severity */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Severity<span className="text-red-500">*</span>
            </label>
            <div className="flex gap-1 bg-gray-200 rounded-lg p-1 w-fit">
              {SEVERITIES.map((s) => (
                <label key={s} className="relative cursor-pointer">
                  <input
                    type="radio"
                    name="severity"
                    value={s}
                    checked={form.severity === s}
                    onChange={() => handleSeverity(s)}
                    className="sr-only"
                  />
                  <div
                    className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      form.severity === s
                        ? 'bg-[#4f7a64] text-white shadow-sm'
                        : 'bg-transparent text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {s}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Short Title<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleField}
              maxLength={120}
              placeholder="Brief summary of the issue"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Description<span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleField}
              rows={5}
              placeholder="Describe what happened and what you expected..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4f7a64]/50 resize-none"
            />
            <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">
              Include only the details needed for the support team to understand the issue.
            </p>
          </div>

          {/* Screenshots */}
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Screenshots <span className="text-gray-400 font-normal">(optional)</span>
            </label>

            {isEditMode && existingAttachments.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-500 mb-1.5">Already attached</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  {existingAttachments.map((att) => (
                    <li key={att.referenceId} className="truncate">
                      {att.fileName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {files.length > 0 && (
              <div className="flex gap-3 mb-3">
                {files.map((f, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={f.preview}
                      alt={`Screenshot ${i + 1}`}
                      className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FiTrash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {attachmentSlotsUsed < MAX_FILES && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <FiUpload className="w-4 h-4" />
                  Add Screenshot
                </button>
              </>
            )}

            {fileError && (
              <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                <FiAlertCircle className="w-3 h-3 flex-shrink-0" />
                {fileError}
              </p>
            )}

            <p className="mt-1.5 text-xs text-gray-500">
              PNG or JPG only. Max 2 files, 5 MB each. Screenshots are visible only to Sena support
              and are not part of the medical record.
            </p>
            <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Screenshots and uploaded files may contain client-identifying or health information.
              Please only upload files that are necessary for support.
            </p>
          </div>

          {/* Submit error */}
          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {submitError}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-[#4f7a64] hover:bg-[#3d6350] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting
                ? isEditMode
                  ? 'Saving...'
                  : 'Submitting...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Submit Report'}
            </button>
          </div>
          </>
          )}
        </form>
        )}
      </div>
    </div>
  );
}
